import type React from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PLANS, PLAN_IDS, USER_CREATABLE_ORG_TYPES, type PlanId } from '@ready/shared';
import { useApi } from '@/lib/api';
import { ORG_TYPE_LABELS } from '@/lib/labels';
import { Button, Card, ErrorText, Field, Input, PageHeader, Select } from '@/components/ui';

/** Sales-led onboarding: set up an organization and hand it to its owner. */
export function AdminNewOrgPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    type: 'university',
    country: 'US',
    planId: 'pilot' as PlanId,
    ownerEmail: '',
  });
  const set =
    (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({
        ...f,
        [k]: k === 'country' ? e.target.value.toUpperCase() : e.target.value,
      }));
  const create = useMutation({
    mutationFn: () => api.admin.createOrg(form as Parameters<typeof api.admin.createOrg>[0]),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Create an organization for a customer"
        description="Use this for agreements made outside the app. The owner gets an email and has access as soon as they sign in."
        back={{ to: '/admin/organizations', label: 'Organizations' }}
      />
      <Card>
        {create.data ? (
          <div className="space-y-3 text-sm">
            <p className="font-medium">Created {create.data.org.name}</p>
            <p className="text-muted-foreground">
              {create.data.ownerInvited
                ? `We emailed ${form.ownerEmail}; they become owner when they first sign in.`
                : `${form.ownerEmail} already had an account and is now the owner.`}
            </p>
            <div className="flex gap-3">
              <Link to={`/organizations/${create.data.org.id}`} className="text-primary">
                Open organization
              </Link>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => {
                  create.reset();
                  setForm((f) => ({ ...f, name: '', ownerEmail: '' }));
                }}
              >
                Create another
              </button>
            </div>
          </div>
        ) : (
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <Field label="Organization name" htmlFor="ac-name">
              <Input id="ac-name" required minLength={2} value={form.name} onChange={set('name')} />
            </Field>
            <Field label="Type" htmlFor="ac-type">
              <Select id="ac-type" value={form.type} onChange={set('type')}>
                {USER_CREATABLE_ORG_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {ORG_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Country" htmlFor="ac-country" hint="Two-letter code, e.g. US or IN">
              <Input
                id="ac-country"
                required
                maxLength={2}
                value={form.country}
                onChange={set('country')}
              />
            </Field>
            <Field label="Plan" htmlFor="ac-plan">
              <Select id="ac-plan" value={form.planId} onChange={set('planId')}>
                {PLAN_IDS.map((p) => (
                  <option key={p} value={p}>
                    {PLANS[p].name} ({PLANS[p].limits.maxMembers.toLocaleString()} members)
                  </option>
                ))}
              </Select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Owner email" htmlFor="ac-owner">
                <Input
                  id="ac-owner"
                  type="email"
                  required
                  value={form.ownerEmail}
                  onChange={set('ownerEmail')}
                />
              </Field>
            </div>
            <div className="flex items-center gap-4 sm:col-span-2">
              <Button type="submit" disabled={create.isPending}>
                Create organization
              </Button>
              <ErrorText error={create.error} />
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
